import { createContext, useContext, useEffect, useState, useRef } from 'react';
import PropTypes from 'prop-types';

import AdUrlService from '@spotahome/marketplace-common/src/utils/ad-url-service';
import { reloadWindowLocation } from '@spotahome/marketplace-common/src/utils/windowHelper';

import getGqlClient from '../clients/graphql/marketplace';

import { FilterUtils } from '../../..';
import { getFacetSearchQuery } from '../queries/graphql/facetSearch';
import { isFilteredByDate } from '../components/utils/filters';

const FiltersContext = createContext({
  handleUpdateFilters: () => {},
  requestFacetSearch: () => {},
  handleUpdatePage: () => {},
  handleUpdateMap: () => {},
  resetFilters: () => {},
  handleUpdatePoi: () => {},
  filters: {},
  facetSearch: {}
});

const parseFlexibleMoveFilters = ({
  flexibleMoveIn,
  flexibleMoveOut,
  ...other
}) => ({
  ...other,
  flexibleMoveIn: flexibleMoveIn === 0 ? '0' : false,
  flexibleMoveOut: flexibleMoveOut === 0 ? '0' : false
});

export const FiltersProvider = ({
  initialFilters = null,
  locationString,
  cityId,
  shouldUpdateUrl = true,
  children
}) => {
  const [filters, setFilters] = useState(initialFilters);
  const [facetSearch, setFacetSearch] = useState({});
  const [page, setPage] = useState(initialFilters?.page || 1);
  const [map, setMap] = useState(!!initialFilters?.map);
  const isFirstRender = useRef(true);

  useEffect(() => {
    if (!cityId) {
      return;
    }
    const requestFacetSearch = async () => {
      try {
        const facetSearchResponse = await getFacetSearchQuery(getGqlClient())(
          cityId,
          FilterUtils.adaptFiltersFromUrl(filters)
        );

        setFacetSearch(facetSearchResponse.data.facetSearch);
      } catch (error) {
        console.error(error);
      }
    };

    requestFacetSearch();
  }, [filters, cityId]);

  useEffect(() => {
    if (isFirstRender.current) {
      isFirstRender.current = false;
      return;
    }

    if (shouldUpdateUrl) {
      AdUrlService.setSearchLocationUrl(
        locationString,
        parseFlexibleMoveFilters({
          ...filters,
          page,
          map
        })
      );
    }
  }, [filters, page, map]);

  const handlePopState = () => {
    reloadWindowLocation();
  };

  useEffect(() => {
    window.addEventListener('popstate', handlePopState);
    return () => window.removeEventListener('popstate', handlePopState);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleUpdateFilters = (newFilters, omitSoldout = false) => {
    const datesWereSelected = isFilteredByDate(filters);
    const datesAreSelected = isFilteredByDate(newFilters);
    const noDatesPreviouslySelected = !datesWereSelected && datesAreSelected;
    const includeBlockedProperties =
      noDatesPreviouslySelected &&
      !newFilters.includeBlockedProperties &&
      !omitSoldout;

    const newFiltersWithSoldOut = {
      ...newFilters,
      ...(includeBlockedProperties ? { includeBlockedProperties: 1 } : {})
    };

    setFilters(newFiltersWithSoldOut);
  };

  const handleUpdatePoi = newPoi => {
    if (newPoi?.lat && newPoi?.lng) {
      setFilters({
        ...filters,
        poiLat: newPoi.lat,
        poiLng: newPoi.lng,
        poiName: newPoi.name,
        poiId: newPoi.placeId,
        sortBy: 'POI'
      });
    } else {
      const { poiLat, poiLng, poiName, poiId, ...filtersWithoutPOI } = filters;
      setFilters({ ...filtersWithoutPOI, sortBy: 'bestMatch' });
    }
  };

  // This function could be in a different context -- ViewSearchContext
  const handleUpdatePage = newPage => {
    setPage(newPage);
  };

  // This function could be in a different context -- ViewSearchContext
  const handleUpdateMap = mapState => {
    setMap(mapState);
  };

  const resetFilters = () => {
    setFilters(FilterUtils.DEFAULT_FILTERS);
  };

  return (
    <FiltersContext.Provider
      value={{
        filters: {
          ...filters,
          page,
          map
        },
        poiState: {
          poiLat: filters.poiLat,
          poiLng: filters.poiLng,
          poiName: filters.poiName,
          poiId: filters.poiId
        },
        handleUpdateFilters,
        handleUpdatePage,
        handleUpdateMap,
        handleUpdatePoi,
        facetSearch,
        resetFilters
      }}
    >
      {children}
    </FiltersContext.Provider>
  );
};

FiltersProvider.propTypes = {
  children: PropTypes.oneOfType([
    PropTypes.arrayOf(PropTypes.node),
    PropTypes.node
  ]).isRequired,
  initialFilters: PropTypes.shape({
    page: PropTypes.number,
    map: PropTypes.string // String because it comes from the url as "true"
  }),
  cityId: PropTypes.string.isRequired,
  locationString: PropTypes.string.isRequired,
  shouldUpdateUrl: PropTypes.bool
};

export const useFiltersContext = () => useContext(FiltersContext);
export const FiltersConsumer = FiltersContext.Consumer;

export default {
  FiltersProvider,
  FiltersConsumer,
  useFiltersContext
};
